import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { Button, Modal, PageTitle } from '../root/components/common'
import { useDispatch, useSelector } from 'react-redux'
import {
    getDevicesByIds,
    getLocationsForDevicesMove,
    massMoveDevices
} from '../root/hooks/use-devices-multiselect/devices-multiselect-api'
import { enqueueSnackbar } from '../root/actions'
import { useQuery } from 'react-query'
import { MOVE_TO_LOCATION_MODAL_STEPS, TRANSFER_MODE } from '../root/constants'
import { OperationModeIcon } from '../root/components/common/operation-mode-icon'
import { TableVirtualized } from '../root/components/common/table-virtualized/table'
import { MoveRightIcon, MapPinIcon, CheckIcon } from 'lucide-react'
import { UserPermissions } from '../../services/permissions'
import { getLocationsTree } from '../locations/selectors'
import { DevicesMoveToLocationSection } from './components/devices-move-to-location-section'
import { shouldWarnAboutRetainedLocationSettingsOverrideForMoves } from '../locations/utils/device-location-settings-override'
import { LocationSettingsOverrideMoveConfirmationModal } from '../locations/components/device-location-settings-override-move-confirmation'

/* eslint-disable */
const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    pageTitle: {
        marginBottom: 40
    },
    btnsWrapper: {
        marginTop: 20,
        display: 'flex',
        justifyContent: 'flex-end'
    }
}))

export const MoveToLocationModal = ({
    onClose,
    open,
    selectedDevicesIds,
    excludedDevicesIds,
    filters,
    clearSelection,
    reloadDevices
}) => {
    const dispatch = useDispatch()
    const classes = useStyles()

    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)
    const p = useSelector(state => state.main.p)

    const [moveCustomerIds, setMoveCustomerIds] = useState([])
    const [currentMoveCustomerId, setCurrentMoveCustomerId] = useState(null)
    const [isNotAssignedStepApproved, setIsNotAssignedStepApproved] =
        useState(false)
    const [isAssignedStepApproved, setIsAssignedStepApproved] = useState(false)
    const [locationsTreesMap, setLocationsTreesMap] = useState({})
    const [moveData, setMoveData] = useState({})
    const [locationSearchStr, setLocationSearchStr] = useState('')
    const [pendingMoveBody, setPendingMoveBody] = useState(null)

    const { data: devicesToMove, isLoading: isLoadingDevices } = useQuery(
        [
            'loadDevicesToMove',
            selectedDevicesIds,
            excludedDevicesIds,
            filters,
            open
        ],
        () => getDevicesByIds(selectedDevicesIds, excludedDevicesIds, filters),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: open,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.getDevicesEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const { data: locationsToMoveTo, isLoading: isLoadingLocationsToMoveTo } =
        useQuery(
            ['loadLocationsToMoveTo', isAssignedStepApproved, open],
            () => getLocationsForDevicesMove(),
            {
                refetchOnWindowFocus: false,
                refetchInterval: false,
                enabled: open && isAssignedStepApproved,
                onError: () => {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.sagasMsgs.commonLoadingError,
                            options: {
                                variant: 'error'
                            }
                        })
                    )
                }
            }
        )

    const onLocationSearch = useCallback(
        (searchStr = '') => {
            if (locationsToMoveTo?.length) {
                setLocationSearchStr(searchStr)
                const lTreesMap = devicesToMoveAssigned.reduce((acc, d) => {
                    if (!acc[d.customer_id]) {
                        acc[d.customer_id] = []
                    }
                    return acc
                }, {})

                let locationsProcessed = [...locationsToMoveTo]

                if (searchStr.trim().length) {
                    locationsProcessed = locationsProcessed.filter(l =>
                        l.name.toLowerCase().includes(searchStr.toLowerCase())
                    )
                }

                for (const l of locationsProcessed) {
                    if (lTreesMap.hasOwnProperty(l.customer_id)) {
                        lTreesMap[l.customer_id].push(l)
                    }
                }

                for (const cId of Object.keys(lTreesMap)) {
                    const { assignedLocationsTree } = getLocationsTree(
                        lTreesMap[cId]
                    )
                    lTreesMap[cId] = assignedLocationsTree
                }
                setLocationsTreesMap(lTreesMap)
            }
        },
        [locationsToMoveTo, devicesToMoveAssigned]
    )

    // TODO: fix bad naming, should be:
    // devicesCanBeUpdated, devicesCanNotBeUpdated
    const { devicesToMoveAssigned, devicesToMoveNotAssigned } = useMemo(() => {
        return {
            devicesToMoveAssigned: devicesToMove?.filter(
                d =>
                    UserPermissions.deviceV2(d.is_assigned, d.customer_id)
                        .update
            ),
            devicesToMoveNotAssigned: devicesToMove?.filter(
                d =>
                    !UserPermissions.deviceV2(d.is_assigned, d.customer_id)
                        .update
            )
        }
    }, [devicesToMove])

    const currentStep = useMemo(() => {
        if (devicesToMove?.length > 0) {
            if (
                devicesToMoveNotAssigned.length > 0 &&
                !isNotAssignedStepApproved
            ) {
                return MOVE_TO_LOCATION_MODAL_STEPS.not_assigned_step
            } else if (
                (devicesToMoveNotAssigned.length === 0 ||
                    isNotAssignedStepApproved) &&
                !isAssignedStepApproved
            ) {
                return MOVE_TO_LOCATION_MODAL_STEPS.assigned_step
            } else if (
                (devicesToMoveNotAssigned.length === 0 ||
                    isNotAssignedStepApproved) &&
                isAssignedStepApproved
            ) {
                return MOVE_TO_LOCATION_MODAL_STEPS.location_select
            }
        }

        return null
    }, [
        devicesToMove,
        devicesToMoveAssigned,
        devicesToMoveNotAssigned,
        isNotAssignedStepApproved,
        isAssignedStepApproved
    ])

    const moveDevices = moveBody => {
        massMoveDevices(moveBody)
            .then(moveResult => {
                setTimeout(() => {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.sagasMsgs.moveDevicesRes,
                            options: {
                                variant: 'success'
                            }
                        })
                    )
                }, 500)
            })
            .catch(() => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            })
            .finally(() => {
                cleanUp()
                reloadDevices(filters)
                onClose()
            })
    }

    const handleConfirm = useCallback(async () => {
        if (currentStep === MOVE_TO_LOCATION_MODAL_STEPS.not_assigned_step) {
            setIsNotAssignedStepApproved(true)
        } else if (currentStep === MOVE_TO_LOCATION_MODAL_STEPS.assigned_step) {
            if (devicesToMoveAssigned?.length > 0) {
                setIsAssignedStepApproved(true)
            }
        } else if (
            currentStep === MOVE_TO_LOCATION_MODAL_STEPS.location_select
        ) {
            const newMoveData = { ...moveData }
            newMoveData[currentMoveCustomerId].devices =
                devicesToMoveAssigned.filter(
                    d => Number(d.customer_id) === currentMoveCustomerId
                )
            if (
                Object.keys(newMoveData).every(
                    cId =>
                        newMoveData[cId].locationId && newMoveData[cId].devices
                )
            ) {
                const moveBody = Object.keys(newMoveData).reduce(
                    (acc, cId) => {
                        acc.customers.push({
                            id: cId,
                            devices: newMoveData[cId].devices.map(d => ({
                                id: d.id,
                                location_id: d.location_id,
                                customer_id: d.customer_id
                            })),
                            location_id: newMoveData[cId].locationId
                        })
                        return acc
                    },
                    {
                        customers: []
                    }
                )

                try {
                    const shouldWarn =
                        await shouldWarnAboutRetainedLocationSettingsOverrideForMoves(
                            Object.values(newMoveData)
                        )
                    if (shouldWarn) {
                        setPendingMoveBody(moveBody)
                        return
                    }
                } catch {
                    dispatch(
                        enqueueSnackbar({
                            message: locale.sagasMsgs.commonLoadingError,
                            options: {
                                variant: 'error'
                            }
                        })
                    )
                    return
                }

                moveDevices(moveBody)
            } else {
                setMoveData(newMoveData)
                const nextCustomerId =
                    moveCustomerIds[
                        moveCustomerIds.indexOf(currentMoveCustomerId) + 1
                    ]
                setCurrentMoveCustomerId(nextCustomerId)
                onLocationSearch('')
            }
        }
        return null
    }, [
        currentStep,
        devicesToMoveAssigned,
        moveCustomerIds,
        currentMoveCustomerId,
        moveData,
        filters
    ])

    const selectLocationId = useCallback(
        lId => {
            const newMoveData = { ...moveData }
            newMoveData[currentMoveCustomerId].locationId = lId
            setMoveData(newMoveData)
        },
        [moveData, currentMoveCustomerId]
    )

    const handleCancel = useCallback(() => {
        cleanUp(true)
        onClose()
    }, [cleanUp, onClose])

    const cleanUp = useCallback(
        (isCancel = false) => {
            setMoveCustomerIds([])
            setCurrentMoveCustomerId(null)
            setIsAssignedStepApproved(false)
            setIsNotAssignedStepApproved(false)
            setLocationsTreesMap({})
            setMoveData({})
            if (!isCancel) {
                clearSelection()
            }
            setLocationSearchStr('')
        },
        [
            setMoveCustomerIds,
            setCurrentMoveCustomerId,
            setIsAssignedStepApproved,
            setIsNotAssignedStepApproved,
            setLocationsTreesMap,
            setMoveData,
            clearSelection,
            setLocationSearchStr
        ]
    )

    const devicesColumns = [
        {
            key: 'mode',
            label: '',
            sortable: false,
            dataType: 'string',
            width: 0.5,
            maxWidth: 60,
            minWidth: 60,
            alignCenter: true,
            render: ({ rowData, cellData }) => {
                const isAssigned = rowData.is_assigned
                if (rowData.hasOwnProperty('transfer_mode')) {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                            <OperationModeIcon
                                operationMode={rowData.operation_mode}
                                locale={locale}
                                size="small"
                            />
                            <div
                                style={{
                                    fontSize: 12
                                }}>
                                {rowData.transfer_mode === TRANSFER_MODE.wifi
                                    ? 'WiFi'
                                    : null}
                                {rowData.transfer_mode === TRANSFER_MODE.lora
                                    ? 'LoRa'
                                    : null}
                                {rowData.transfer_mode ===
                                TRANSFER_MODE.cellular
                                    ? locale.devices.cellular
                                    : null}
                            </div>
                        </div>
                    )
                } else {
                    return (
                        <OperationModeIcon
                            operationMode={rowData.operation_mode}
                            locale={locale}
                        />
                    )
                }
            }
        },
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            maxWidth: 100,
            render: ({ rowData }) => {
                return rowData.name
            }
        },
        {
            key: 'serial_number',
            label: locale.locations.deviceSerial,
            sortable: true,
            dataType: 'string',
            width: 1,
            maxWidth: 100,
            render: ({ rowData }) => rowData.serial_number
        },
        {
            key: 'location_name',
            label: locale.devices.location,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            maxWidth: 140,
            render: ({ rowData, cellData }) => rowData.location_name
        },
        {
            key: 'customer_name',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 90,
            render: ({ rowData }) => {
                return rowData.customer_name
            }
        },
        {
            key: 'role_name',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 90,
            render: ({ rowData }) => {
                return p[rowData.customer_id]?.role_name || ''
            }
        }
    ]

    const notAssignedColumns = [
        ...devicesColumns,
        {
            key: 'is_assigned',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 80,
            render: ({ rowData }) => {
                return rowData.is_assigned
                    ? locale.common.assigned
                    : locale.devices.notAssigned
            }
        }
    ]

    const assignedColumns = [
        ...devicesColumns,
        {
            key: 'moving_access',
            label: '',
            sortable: false,
            dataType: 'string',
            // alignCenter: true,
            width: 1,
            maxWidth: 80,
            render: ({ rowData }) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <MoveRightIcon size={22} color={theme.primary} />
                        <MapPinIcon size={22} color={theme.primary} />
                        <CheckIcon size={22} color={theme.primary} />
                    </div>
                )
            }
        }
    ]

    const isConfirmButtonDisabled = useMemo(() => {
        if (
            currentStep === MOVE_TO_LOCATION_MODAL_STEPS.assigned_step &&
            devicesToMoveAssigned.length === 0
        ) {
            return true
        } else if (
            currentStep === MOVE_TO_LOCATION_MODAL_STEPS.location_select &&
            currentMoveCustomerId
        ) {
            if (!moveData[currentMoveCustomerId].locationId) {
                return true
            }
        }
        return false
    }, [currentMoveCustomerId, moveData, currentStep])

    useEffect(() => {
        if (locationsToMoveTo?.length) {
            const lTreesMap = devicesToMoveAssigned.reduce((acc, d) => {
                if (!acc[d.customer_id]) {
                    acc[d.customer_id] = []
                }
                return acc
            }, {})

            const newMoveData = Object.keys(lTreesMap).reduce(
                (acc, cId) => ({ ...acc, [cId]: {} }),
                {}
            )

            for (const l of locationsToMoveTo) {
                if (lTreesMap.hasOwnProperty(l.customer_id)) {
                    lTreesMap[l.customer_id].push(l)
                }
            }

            for (const cId of Object.keys(lTreesMap)) {
                const { assignedLocationsTree } = getLocationsTree(
                    lTreesMap[cId]
                )
                lTreesMap[cId] = assignedLocationsTree
            }
            setLocationsTreesMap(lTreesMap)
            const customerIds = Object.keys(lTreesMap).map(Number)
            setMoveCustomerIds(customerIds)
            setCurrentMoveCustomerId(customerIds[0])
            setMoveData(newMoveData)
        }
    }, [devicesToMoveAssigned, locationsToMoveTo])

    return (
        <Modal
            size={
                currentStep === MOVE_TO_LOCATION_MODAL_STEPS.location_select
                    ? 'xl'
                    : 'lg'
            }
            onClose={handleCancel}
            open={open}
            className={classes.root}>
            <PageTitle
                title={locale.devices.moveDToL}
                className={classes.pageTitle}
            />
            {isLoadingDevices ? (
                <div style={{ height: '50vh' }}>
                    <div
                        style={{
                            display: 'flex',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        <CircularProgress size={20} color={theme.primary} />
                    </div>
                </div>
            ) : null}
            {!isLoadingDevices && currentStep ? (
                <>
                    {currentStep ===
                    MOVE_TO_LOCATION_MODAL_STEPS.not_assigned_step ? (
                        <>
                            <div>
                                <span style={{ fontSize: 20 }}>ⓘ</span>
                                {locale.devices.youAreNotAbleToMoveTheseDevices}
                            </div>
                            <div style={{ marginTop: 8 }}>
                                {locale.devices.devicesAmount}:{' '}
                                {devicesToMoveNotAssigned.length}
                            </div>
                            <div style={{ height: 300, marginTop: 12 }}>
                                <TableVirtualized
                                    data={devicesToMoveNotAssigned}
                                    columns={notAssignedColumns}
                                    disableHeader={true}
                                />
                            </div>
                        </>
                    ) : null}
                    {currentStep ===
                    MOVE_TO_LOCATION_MODAL_STEPS.assigned_step ? (
                        <>
                            {devicesToMoveAssigned.length === 0 ? (
                                <div>{locale.devices.noDevicesYouCanMove}</div>
                            ) : null}
                            {devicesToMoveAssigned.length > 0 ? (
                                <>
                                    <div>
                                        <span style={{ fontSize: 20 }}>ⓘ</span>{' '}
                                        {locale.devices.devicesReadyToMove}
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        {locale.devices.devicesAmount}:{' '}
                                        {devicesToMoveAssigned.length}
                                    </div>
                                    <div style={{ height: 300, marginTop: 12 }}>
                                        <TableVirtualized
                                            data={devicesToMoveAssigned}
                                            columns={assignedColumns}
                                            disableHeader={true}
                                        />
                                    </div>
                                </>
                            ) : null}
                        </>
                    ) : null}
                    {currentStep ===
                        MOVE_TO_LOCATION_MODAL_STEPS.location_select &&
                    currentMoveCustomerId ? (
                        <DevicesMoveToLocationSection
                            devices={devicesToMoveAssigned.filter(
                                d =>
                                    Number(d.customer_id) ===
                                    currentMoveCustomerId
                            )}
                            locationsTree={
                                locationsTreesMap[currentMoveCustomerId]
                            }
                            customerId={currentMoveCustomerId}
                            selectLocationId={selectLocationId}
                            onLocationSearch={onLocationSearch}
                            locationSearchStr={locationSearchStr}
                        />
                    ) : null}
                </>
            ) : null}
            <div className={classes.btnsWrapper}>
                <Button
                    style={{ marginRight: 8 }}
                    onClick={handleConfirm}
                    disabled={isConfirmButtonDisabled}>
                    {locale.common.confirm}
                </Button>
                <Button reverted onClick={handleCancel}>
                    {locale.common.cancel}
                </Button>
            </div>
            <LocationSettingsOverrideMoveConfirmationModal
                open={Boolean(pendingMoveBody)}
                onConfirm={() => {
                    moveDevices(pendingMoveBody)
                    setPendingMoveBody(null)
                }}
                onCancel={() => setPendingMoveBody(null)}
                locale={locale}
            />
        </Modal>
    )
}
/* eslint-enable */
